// src/utils/assetTableRegistry.js
// -----------------------------------------------------------------------------
// Centralized registry for asset-specific “virtual columns” in the submissions
// table. The ColumnDraftManager uses this registry to render dynamic toggles;
// the table renderers use the `role` (and associated metadata) to render cells.
//
// ♻️ Design goals
//  - One place to define what columns exist (static + org-defined).
//  - Include ALL AUF fields (not just promoted) so orgs can choose any.
//  - Keep “promoted” as a UI/metadata flag only (useful for badges/ordering).
//  - Stable, explicit column IDs for storage (composeable, parseable).
//  - Extensible for 3rd-party DAM vendors (e.g., Bynder) via provider sections.
//  - No React here; pure utilities & data contracts.
//
// 🧩 How it fits in
//  - ColumnDraftManager asks this registry, per attach/image field, which
//    columns are available to add (pills).
//  - The manager writes items to columnOrder with kind/role/ufi(+aufUfi).
//  - DraftSubmissionTable renders items by `role`, using attach UFI (and
//    aufUfi for AUF-driven fields) to read appropriate values.
//
// -----------------------------------------------------------------------------


/**
 * Column renderer roles your table should support.
 * Extend sparingly; the renderer switch should map each role to a render fn.
 */
export const VIRTUAL_ROLES = {
  THUMB: 'thumb',           // small thumbnail + id
  VERSION: 'version',       // asset versionNumber
  ASSET_TYPE: 'assetType',  // asset assetType
  ASSET_ID: 'assetId',      // asset id (useful for export/debug)
  AUF_FIELD: 'aufField',    // value resolved via AUF UFI (promoted or metadata.responses)
  // Provider-specific roles can be namespaced (optional, see provider defs section)
  PROVIDER_FIELD: 'providerField',
};

/**
 * A single “virtual column” definition returned by this registry.
 *
 * @typedef {Object} AssetColumnDef
 * @property {string} key                // unique within the registry (human-oriented)
 * @property {string} label              // pill label in the ColumnDraftManager
 * @property {string} role               // one of VIRTUAL_ROLES
 * @property {('attach'|'image')[]} fieldTypes // which field types this applies to
 * @property {boolean} defaultVisible    // default when first added
 * @property {string} [help]             // optional tooltip
 * @property {string} [ufi]              // AUF UFI when role === 'aufField'
 * @property {boolean} [promoted=false]  // AUF field is promoted (useful for UI badge)
 * @property {Object}  [provider]        // provider metadata (see below)
 * @property {string}  [provider.name]   // e.g., 'bynder'
 * @property {string}  [provider.key]    // provider-specific field key (e.g., 'license')
 */

/**
 * Utility: build a stable, parseable virtual column ID for persistence in
 * addToSubmissionsTable.columnOrder[].id.
 *
 * ID convention (field-scope virtuals):
 *   `${role}@${attachUfi}`                  // e.g. 'thumb@ATTACH_UFI'
 * For AUF-sourced columns:
 *   `${role}@${attachUfi}@${aufUfi}`        // e.g. 'aufField@ATTACH_UFI@AUF_UFI'
 * For provider fields:
 *   `${role}:${providerKey}@${attachUfi}`   // e.g. 'providerField:bynder.license@ATTACH_UFI'
 */
export function makeVirtualColumnId({ role, attachUfi, aufUfi, provider }) {
  if (role === VIRTUAL_ROLES.AUF_FIELD && aufUfi) {
    return `${role}@${attachUfi}@${aufUfi}`;
  }
  if (role === VIRTUAL_ROLES.PROVIDER_FIELD && provider?.key) {
    return `${role}:${String(provider.key)}@${attachUfi}`;
  }
  return `${role}@${attachUfi}`;
}

/**
 * Utility: parse a virtual column ID back into components.
 * Returns { role, attachUfi, aufUfi?, providerKey? } or null if unknown pattern.
 */
export function parseVirtualColumnId(id) {
  if (typeof id !== 'string') return null;

  // Provider form: 'providerField:bynder.license@ATTACH_UFI'
  if (id.startsWith(`${VIRTUAL_ROLES.PROVIDER_FIELD}:`)) {
    const [roleAndKey, attachUfi] = id.split('@');
    const [, providerKey] = roleAndKey.split(':'); // e.g., 'bynder.license'
    return { role: VIRTUAL_ROLES.PROVIDER_FIELD, attachUfi, providerKey };
  }

  // AUF form: 'aufField@ATTACH_UFI@AUF_UFI'
  if (id.startsWith(`${VIRTUAL_ROLES.AUF_FIELD}@`)) {
    const [, attachUfi, aufUfi] = id.split('@');
    return { role: VIRTUAL_ROLES.AUF_FIELD, attachUfi, aufUfi };
  }

  // Field-scope form: 'thumb@ATTACH_UFI', 'version@ATTACH_UFI', etc.
  const [role, attachUfi] = id.split('@');
  if (!role || !attachUfi) return null;
  return { role, attachUfi };
}

/**
 * Built-in, provider-agnostic asset columns always available.
 * Policy and org flags can later control visibility or availability.
 *
 * @param {{ includeAssetId?: boolean }} [policy]
 * @returns {AssetColumnDef[]}
 */
export function getStaticAssetColumnDefs(policy = {}) {
  const { includeAssetId = true } = policy;

  /** @type {AssetColumnDef[]} */
  const staticDefs = [
    {
      key: 'version',
      label: 'Version',
      role: VIRTUAL_ROLES.VERSION,
      fieldTypes: ['attach', 'image'],
      defaultVisible: false,
      help: 'Asset version number',
    },
    {
      key: 'assetType',
      label: 'Type',
      role: VIRTUAL_ROLES.ASSET_TYPE,
      fieldTypes: ['attach', 'image'],
      defaultVisible: false,
      help: 'Asset type (icon, banner, screenshot, …)',
    },
  ];

  if (includeAssetId) {
    staticDefs.push({
      key: 'assetId',
      label: 'Asset ID',
      role: VIRTUAL_ROLES.ASSET_ID,
      fieldTypes: ['attach', 'image'],
      defaultVisible: false,
      help: 'Stable asset identifier (useful for export/debug)',
    });
  }

  return staticDefs;
}

/**
 * Normalize AUF fields into a minimal array of { ufi, label, type }.
 * Accepts either the decompressed AUF `fieldsMetadata` object (by fieldId)
 * or an array of field objects; returns a flat array.
 *
 * @param {Record<string, any>|any[]} aufFields
 * @returns {{ufi:string,label:string,type:string}[]}
 */
export function normalizeAufFields(aufFields) {
  if (!aufFields) return [];
  const out = [];

  // Case 1: fieldsMetadata map by fieldId
  if (!Array.isArray(aufFields)) {
    for (const [, f] of Object.entries(aufFields)) {
      if (!f) continue;
      const ufi = String(f.ufi || '').trim();
      if (!ufi) continue;
      out.push({
        ufi,
        label: String(f.label || f.name || f.id || ufi),
        type : String(f.type || 'text'),
      });
    }
    return out;
  }

  // Case 2: already an array
  for (const f of aufFields) {
    if (!f) continue;
    const ufi = String(f.ufi || '').trim();
    if (!ufi) continue;
    out.push({
      ufi,
      label: String(f.label || f.name || f.id || ufi),
      type : String(f.type || 'text'),
    });
  }
  return out;
}

/**
 * Build column defs for **all AUF fields** (not just promoted).
 * Promoted status is indicated for UI (badge/ordering) but does not filter.
 *
 * @param {{keys?: string[], arrayKeys?: string[], dateKeys?: string[]}} promotionConfig
 * @param {Record<string, string>} labelsMap   // UFI → label (optional; falls back to AUF field label/ufi)
 * @param {{ ufi:string,label:string,type:string }[]} normalizedAufFields
 * @returns {AssetColumnDef[]}
 */
export function getAufFieldDefs(promotionConfig = {}, labelsMap = {}, normalizedAufFields = []) {
  const promotedSet = new Set([
    ...(Array.isArray(promotionConfig.keys) ? promotionConfig.keys : []),
    ...(Array.isArray(promotionConfig.arrayKeys) ? promotionConfig.arrayKeys : []),
    ...(Array.isArray(promotionConfig.dateKeys) ? promotionConfig.dateKeys : []),
  ]);

  /** @type {AssetColumnDef[]} */
  const defs = [];
  for (const f of normalizedAufFields) {
    // AUF fields are attach/image-agnostic in terms of definition; we scope them to attach/image displays.
    defs.push({
      key: `auf:${f.ufi}`,
      label: labelsMap[f.ufi] || f.label || f.ufi,
      role: VIRTUAL_ROLES.AUF_FIELD,
      fieldTypes: ['attach', 'image'],
      defaultVisible: false,            // designer chooses explicitly
      ufi: f.ufi,                       // which AUF UFI this column reads
      promoted: promotedSet.has(f.ufi), // helps badge/ordering in UI
      help: 'Org-defined AUF field',
    });
  }
  return defs;
}

/**
 * Optional: provider-specific columns (e.g., Bynder). This returns *suggestions*
 * that can be toggled like any other column. Your table renderer must handle
 * role === PROVIDER_FIELD and read values accordingly (from a provider resolver).
 *
 * @param {{ bynder?: boolean }} providerFlags
 * @returns {AssetColumnDef[]}
 */
export function getProviderColumnDefs(providerFlags = {}) {
  /** @type {AssetColumnDef[]} */
  const defs = [];

  if (providerFlags?.bynder) {
    // Example columns — you can add more or gate by policy
    defs.push({
      key: 'bynder.license',
      label: 'Bynder License',
      role: VIRTUAL_ROLES.PROVIDER_FIELD,
      fieldTypes: ['attach', 'image'],
      defaultVisible: false,
      provider: { name: 'bynder', key: 'license' },
      help: 'License metadata from Bynder',
    });
    defs.push({
      key: 'bynder.collection',
      label: 'Bynder Collection',
      role: VIRTUAL_ROLES.PROVIDER_FIELD,
      fieldTypes: ['attach', 'image'],
      defaultVisible: false,
      provider: { name: 'bynder', key: 'collection' },
      help: 'Collection name from Bynder',
    });
  }

  return defs;
}

/**
 * Build the final registry (static + AUF fields + provider suggestions).
 *
 * @param {{
 *   organizationId?: string,
 *   aufFields?: Record<string, any>|any[],                  // AUF fields (decompressed); map or array
 *   promotionConfig?: {keys?: string[], arrayKeys?: string[], dateKeys?: string[]}, // org promotion config
 *   labelsMap?: Record<string,string>,                      // AUF UFI → human label
 *   providerFlags?: Record<string, boolean>,                // e.g., { bynder:true }
 *   staticPolicy?: { includeAssetId?: boolean },            // expose Asset ID virtual
 *   filter?: { includeFieldTypes?: ('attach'|'image')[] },  // default ['attach','image']
 * }} opts
 * @returns {AssetColumnDef[]}
 */
export function getAssetTableRegistry(opts = {}) {
  const {
    organizationId = null,
    aufFields = null,
    promotionConfig = {},
    labelsMap = {},
    providerFlags = {},
    staticPolicy = {},
    filter = { includeFieldTypes: ['attach', 'image'] },
  } = opts;

  const includeTypes = Array.isArray(filter?.includeFieldTypes) && filter.includeFieldTypes.length
    ? new Set(filter.includeFieldTypes)
    : new Set(['attach', 'image']);

  // Static built-ins
  const staticDefs = getStaticAssetColumnDefs(staticPolicy);

  // AUF fields → ALL fields (not just promoted)
  const normalizedAufFields = normalizeAufFields(aufFields);
  const aufDefs = getAufFieldDefs(promotionConfig, labelsMap, normalizedAufFields);

  // Provider suggestions
  const providerDefs = getProviderColumnDefs(providerFlags);

  // Merge
  const combined = [...staticDefs, ...aufDefs, ...providerDefs];

  // Filter by attach/image applicability (for table per-field toggles)
  const filtered = combined.filter(def =>
    def.fieldTypes.some(t => includeTypes.has(t))
  );

  // Optional: stable sort — static first, then promoted AUF, then non-promoted AUF, then providers
  filtered.sort((a, b) => {
    const order = (def) => {
      if (!def || !def.role) return 99;
      // (thumb removed)
      if (def.role === VIRTUAL_ROLES.VERSION) return 1;
      if (def.role === VIRTUAL_ROLES.ASSET_TYPE) return 2;
      if (def.role === VIRTUAL_ROLES.ASSET_ID) return 3;
      if (def.role === VIRTUAL_ROLES.AUF_FIELD && def.promoted) return 4;
      if (def.role === VIRTUAL_ROLES.AUF_FIELD && !def.promoted) return 5;
      if (def.role === VIRTUAL_ROLES.PROVIDER_FIELD) return 6;
      return 98;
    };
    return order(a) - order(b);
  });


  return filtered;
}
